import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useBluelagoon } from "@bluelagoon";
import { Card, CardContent } from "@shadcn-in-nx/ui/card";
import { Button } from "@shadcn-in-nx/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@shadcn-in-nx/ui/table";
import { Loader2, Coins } from "lucide-react";

const RANGES = [7, 30, 90];
const GROUPS = ["user", "workflow", "model"];

export default function CreditsTab({ companyId, details }) {
    const { t } = useTranslation();
    const [days, setDays] = useState(30);
    const [groupBy, setGroupBy] = useState("user");
    const [breakdown, setBreakdown] = useState(null);
    const { call: fetchBreakdown, isLoading } = useBluelagoon({ method: "getCompanyCreditBreakdown" });

    const load = useCallback(() => {
        fetchBreakdown({ companyId, groupBy, days })
            .then((res) => setBreakdown(res?.data || null))
            .catch(() => setBreakdown(null));
    }, [fetchBreakdown, companyId, groupBy, days]);

    useEffect(() => { load(); }, [load]);

    return (
        <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Card><CardContent className="p-4">
                    <p className="text-xs text-muted-foreground">{t("admin.manageCompany.credits.balance", "Current balance")}</p>
                    <p className="text-2xl font-semibold flex items-center gap-2 mt-1">
                        <Coins className="h-5 w-5 text-yellow-500" />{(details?.currentCredits || 0).toFixed(2)}
                    </p>
                </CardContent></Card>
                <Card><CardContent className="p-4">
                    <p className="text-xs text-muted-foreground">{t("admin.manageCompany.credits.usedInRange", "Used in last {{n}} days", { n: days })}</p>
                    <p className="text-2xl font-semibold flex items-center gap-2 mt-1">
                        <Coins className="h-5 w-5 text-orange-500" />{(breakdown?.totalCredits || 0).toFixed(2)}
                    </p>
                </CardContent></Card>
            </div>
            <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-muted-foreground mr-1">{t("admin.manageCompany.credits.range", "Range")}:</span>
                {RANGES.map((r) => (
                    <Button key={r} size="sm" variant={days === r ? "default" : "outline"} onClick={() => setDays(r)}>
                        {t("admin.manageCompany.credits.daysShort", "{{n}}d", { n: r })}
                    </Button>
                ))}
                <span className="text-xs text-muted-foreground ml-4 mr-1">{t("admin.manageCompany.credits.groupBy", "Group by")}:</span>
                {GROUPS.map((g) => (
                    <Button key={g} size="sm" variant={groupBy === g ? "default" : "outline"} onClick={() => setGroupBy(g)}>
                        {t(`admin.manageCompany.credits.group.${g}`, g)}
                    </Button>
                ))}
            </div>
            {groupBy === "workflow" && breakdown?.partialAttribution && (
                <p className="text-xs text-muted-foreground italic">
                    {t(
                        "admin.manageCompany.credits.workflowNoteAttributed",
                        "Workflow attribution covers credits spent inside step-chat conversations only. {{attributed}} of {{total}} credits attributed; the remainder is proxy / browser-agent / embedding usage.",
                        {
                            attributed: (breakdown?.attributedCredits || 0).toFixed(2),
                            total: (breakdown?.totalCredits || 0).toFixed(2),
                        },
                    )}
                </p>
            )}
            {groupBy !== "workflow" && breakdown?.partialAttribution && (
                <p className="text-xs text-muted-foreground italic">
                    {t(
                        "admin.manageCompany.credits.truncatedNote",
                        "Showing top {{n}} rows. Headline total reflects the full ledger.",
                        { n: (breakdown?.rows || []).length },
                    )}
                </p>
            )}
            {isLoading ? (
                <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
            ) : (
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>{groupBy === "user"
                                ? t("admin.manageCompany.credits.user", "User")
                                : groupBy === "workflow"
                                ? t("admin.manageCompany.credits.workflow", "Workflow")
                                : t("admin.manageCompany.credits.model", "Model")}
                            </TableHead>
                            <TableHead className="text-right">{t("admin.manageCompany.credits.calls", "Calls")}</TableHead>
                            <TableHead className="text-right">{t("admin.manageCompany.credits.tokens", "Tokens (in/out)")}</TableHead>
                            <TableHead className="text-right">{t("admin.manageCompany.credits.creditsUsed", "Credits used")}</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {(breakdown?.rows || []).length === 0 ? (
                            <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-6">
                                {t("admin.manageCompany.credits.empty", "No usage in this range")}
                            </TableCell></TableRow>
                        ) : (breakdown?.rows || []).map((row, idx) => (
                            <TableRow key={`${row.key}-${idx}`}>
                                <TableCell>
                                    <div className="font-medium text-sm">{row.label}</div>
                                    {row.user?.email && <div className="text-xs text-muted-foreground font-mono">{row.user.email}</div>}
                                </TableCell>
                                <TableCell className="text-right font-mono text-xs">{row.callCount}</TableCell>
                                <TableCell className="text-right font-mono text-xs">{row.inputTokens.toLocaleString()} / {row.outputTokens.toLocaleString()}</TableCell>
                                <TableCell className="text-right font-semibold">{row.creditsUsed.toFixed(2)}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}
        </div>
    );
}
